import { SURFACE } from "../common/designTokens";

/**
 * Plain-table styling for the BDC screens. The control sheet runs to ~2,200 stores × ~20
 * SKUs, so its cells are bare <td>s styled once from the wrapping Box rather than MUI
 * TableCell components (one emotion class per cell would dominate the render).
 */
export const CARD_SX = {
  boxShadow: "none",
  borderRadius: SURFACE.radius,
  border: `1px solid ${SURFACE.line}`,
  p: 0,
  bgcolor: SURFACE.card,
} as const;

export const TAG_COLORS = {
  ok: { bg: "#E6F4EC", fg: "#1E8E5A" },
  warn: { bg: "#FFF3DC", fg: "#B86E00" },
  /** Between warn and crit: a store that missed several orders. */
  high: { bg: "#FCE6D6", fg: "#9A3A0A" },
  crit: { bg: "#FDECEC", fg: "#C62828" },
  info: { bg: "#E9F0FB", fg: "#2F5DA8" },
  mut: { bg: "#EEF0F3", fg: "#6B7080" },
} as const;

export type TagTone = keyof typeof TAG_COLORS;

const tagRules = Object.fromEntries(
  Object.entries(TAG_COLORS).map(([tone, c]) => [
    `& .bdc-tag.${tone}`,
    { background: c.bg, color: c.fg },
  ]),
);

export const TABLE_SX = {
  "& table.bdc-t": {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8125rem",
  },
  "& .bdc-t th": {
    textAlign: "left",
    fontWeight: 500,
    color: SURFACE.tx2,
    background: SURFACE.head,
    borderBottom: `1px solid ${SURFACE.line}`,
    padding: "8px 10px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  "& .bdc-t td": {
    borderBottom: `1px solid ${SURFACE.line2}`,
    padding: "7px 10px",
    verticalAlign: "middle",
  },
  "& .bdc-t tr.click": { cursor: "pointer" },
  "& .bdc-t tr.click:hover td": { background: "#FFFBF3" },
  "& .bdc-t .n": {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  "& .bdc-t .c": { textAlign: "center" },
  /** A deduction such as a credit note, in the critical colour. */
  "& .bdc-t .neg": { color: TAG_COLORS.crit.fg },
  "& .bdc-t tfoot td": {
    fontWeight: 600,
    background: SURFACE.head,
    borderTop: `1px solid ${SURFACE.line}`,
  },
  "& .mono": { fontFamily: "ui-monospace, Consolas, monospace", fontSize: "0.75rem" },
  "& .sub": { color: SURFACE.tx2, fontSize: "0.75rem" },
  "& .dc": { fontWeight: 600 },
  "& .bdc-tag": {
    display: "inline-block",
    borderRadius: "6px",
    padding: "1px 8px",
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
  ...tagRules,
  // ---- control sheet pivot ----
  "& .bdc-pv th.sk": {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    height: 118,
    textAlign: "left",
    padding: "6px 4px",
    fontSize: "0.75rem",
    verticalAlign: "bottom",
  },
  "& .bdc-pv td.q": {
    padding: "5px 4px",
    minWidth: 30,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },
  "& .bdc-pv td.z": { color: "#C3C6CE" },
  "& .bdc-pv tr.lh td": { background: "#F1F2F5", fontWeight: 600 },
  "& .bdc-pv tr.ls td": { background: "#FFF4E2", fontWeight: 600 },
  "& .bdc-pv tr.gt td": { background: "#363A44", color: "#fff", fontWeight: 600 },
  "& .bdc-pv tr.cr td": { background: SURFACE.head, fontWeight: 600 },
} as const;

/** Scroll box for long tables; the sticky header rides inside it. */
export const SCROLL_SX = {
  overflow: "auto",
  maxHeight: "calc(100vh - 320px)",
  minHeight: 200,
} as const;
